<template>
    <div class="p-14 flex flex-col items-center justify-center">
        <AddPhoneIcon class="mb-8"/>

    <p class="text-body-5 font-medium text-default-1 mb-3">
      Add new mobile phone number
    </p>

        <p class="text-body-6 text-default-1 text-center mb-6">
            After entering the phone number, there will be a verification process
        </p>

        <PhoneInput
                class="w-full"
                :region-code="phoneNumberRegion"
                v-model:value="phoneNumber.value.value"
                v-model:valid="isPhoneNumberValid"
                :error="phoneNumberError.contact_field"
                @input="resetAllErrors"
        />

        <CoreButton
                buttonType="primary"
                :label="isAdmin ? callToActionMessage : 'Send verification code'"
                size="default"
                @click="sendCode"
                @keyup.enter="sendCode"
                :loading="buttonLoader"
                class="mt-6"
                :disabled="buttonLoader || phoneNumber.value.value === '' || !!phoneNumberError.contact_field || !isPhoneNumberValid"
        />
    </div>
</template>

<script setup>
import {computed, defineEmits, onBeforeMount, ref} from "vue";
import {useField, useForm} from "vee-validate";
import * as yup from "yup";

import CoreButton from '@/components/core/Button.vue';
import AddPhoneIcon from '@/components/icons/phone/Add.vue';
import PhoneInput from "@/components/inputs/PhoneInput.vue";
import {useOnboarding} from "@/stores/OnboardingStore";
import {useLocale} from "@/stores/LocaleStore";
import {storeToRefs} from "pinia";
import {useAuth} from "@/stores/AuthStore";

const {isAdmin} = useAuth();

const emit = defineEmits(['sendCode']);

const isPhoneNumberValid = ref(false);

const props = defineProps({
    buttonLoader: {
        type: Boolean,
        default: false,
    },
    callToActionMessage: {
        type: String,
        default: 'Change',
    },
    compareToOriginalPhoneNumber: {
        type: Boolean,
        default: false,
    },
    originalPhoneNumber: {
        type: String,
        default: '',
    },
})

const {errors: onBoardingErrors, resetAllErrors} = useOnboarding();

const {meta} = useForm({
    initialValues: {
        phoneNumber: '',
    },
});
const {phoneNumberRegion} = storeToRefs(useLocale());

const phoneSchema = computed(() => {
    let schema = yup
        .string()
        .phone()
        .label("The mobile phone number");

    // Only apply the custom test if the flag is true
    if (props.compareToOriginalPhoneNumber) {
        schema = schema.test(
            'is-different',
            'The mobile phone number must be different from the original mobile phone number',
            function (value) {
                return value.replace(/[\s\-()]/g, '') !== props.originalPhoneNumber;
            }
        );
    }

    return schema;
});

const phoneNumber = useField("phone_number", phoneSchema.value);

const phoneNumberError = computed(() => {
    return {
        contact_field: onBoardingErrors.new_phone_number ?? (phoneNumber.errorMessage.value ?? null)
    };
});

function sendCode() {
    resetAllErrors();

    if (!meta.value.valid) {
        return
    }

    emit('sendCode', phoneNumber.value.value.replace(/[^\d+]/g, ''));
}



onBeforeMount(() =>
{
    resetAllErrors();
    phoneNumber.value.value = '';
})
</script>
